import React, { useState } from 'react';
import { Query, withApollo } from 'react-apollo';
import dayjs from 'dayjs';
import DayPicker from 'react-day-picker';
import { Alert, Button, Modal, message } from 'antd';
import { GET_CURRENT_USER, GET_USER_DETAIL } from '../../api/user';
import { BAN_USER, GET_BANNED_USERS, GET_BANNED_ANOM_USERS } from '../../api/banned';
import BanUserForm from '../../containers/BannedList/BanUserForm';
import 'react-day-picker/lib/style.css';
import Loading from '../Loading';
import './index.css';

function UserBanAction({ client, userId, nickname }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const formRef = React.useRef(null);

  const submit = () => {
    if (!formRef.current || submitting) return;
    formRef.current.props.form.validateFields(async (error, values) => {
      if (error) return;
      setSubmitting(true);
      try {
        await client.mutate({
          mutation: BAN_USER,
          variables: { ...values, _id: userId, reason: values.reason.trim() },
          refetchQueries: [{ query: GET_BANNED_USERS }, { query: GET_BANNED_ANOM_USERS }],
        });
        setOpen(false);
        message.success('处罚已保存');
      } catch (error) {
        message.error(error.message || '封禁失败');
      } finally {
        setSubmitting(false);
      }
    });
  };

  return (
    <Query query={GET_CURRENT_USER}>
      {({ data }) => {
        if (!data || !data.currentUser || data.currentUser.role !== 'admin') return null;
        return (
          <section className="user-moderation-actions" aria-label="账号管理">
            <span>账号管理</span>
            <Button icon="lock" onClick={() => setOpen(true)}>封禁用户</Button>
            <Modal title={`封禁用户 · ${nickname || userId}`} visible={open}
              okText="确认处罚" okType="danger" cancelText="取消" destroyOnClose confirmLoading={submitting}
              onOk={submit} onCancel={() => !submitting && setOpen(false)}>
              <BanUserForm bannedUser={{ _id: userId }} onSubmit={submit}
                onSetRef={ref => { formRef.current = ref; }} />
            </Modal>
          </section>
        );
      }}
    </Query>
  );
}

function UserDetail({ _id, type, _handleUpdate, allowBan, client, onViewHistory, onProfileLoaded }) {
  let [dateValue, setDateValue] = useState('');
  let [oldId, setID] = useState('');

  if (!_id) return null;
  if (!type) return null;

  if (!oldId || oldId !== _id) {
    setDateValue('');
    setID(_id);
  }

  const handleDateClick = day => {
    _handleUpdate('selectedDate', day);
    setDateValue(day);
  };
  return (
    <Query
      query={GET_USER_DETAIL}
      variables={{ _id }}
      fetchPolicy="no-cache"
      errorPolicy="all"
      onCompleted={onProfileLoaded && (data => {
        if (data && data.user && data.user._id === _id) onProfileLoaded(data.user);
      })}
    >
      {({ loading, error, data, refetch }) => {
        if (loading) return <Loading />;
        if (error && !(data && data.user)) {
          return (
            <Alert
              type="error"
              showIcon
              message="Failed to load user details"
              description={
                <span>
                  The user data may be temporarily unavailable.{' '}
                  <Button size="small" onClick={() => refetch()}>
                    Retry
                  </Button>
                </span>
              }
            />
          );
        }
        const {
          createdAt,
          updatedAt,
          avatarUrl,
          nickname,
          gender,
          celebrity,
          grade,
          major,
          school,
          identity,
          phone,
          followers = [],
          following = [],
          cartons = [],
          agreeTerms,
        } = data.user || {};
        return (
          <div className="user-details-container">
            {error && (
              <Alert
                type="warning"
                showIcon
                message="Some related user data could not be loaded"
                style={{ marginBottom: '16px' }}
              />
            )}
            <div className="user-details-top-dates">
              <div>
                <span style={{ fontWeight: 'bold', color: '#2B9EB3' }}>
                  Created at:
                </span>{' '}
                {dayjs(createdAt).format('YYYY-MM-DD')}
              </div>
              <div>
                <span style={{ fontWeight: 'bold', color: '#2B9EB3' }}>
                  Last active:
                </span>{' '}
                {dayjs(updatedAt).format('YYYY-MM-DD')}
              </div>
            </div>
            <div className="user-details-top-container">
              <img className="user-details-top-avatar" src={avatarUrl} alt="" />
              <div className="user-details-top-details">
                <div style={{ fontWeight: 'bold' }}>
                  {nickname}
                  <div
                    className="user-details-bubble"
                    style={{
                      border: '0px',
                      color: gender === 'male' ? '#1C5D99' : '#C71453',
                      fontWeight: 'bold',
                      marginRight: '0px',
                    }}
                  >
                    {gender}
                  </div>
                  <div
                    className="user-details-bubble"
                    style={{
                      border: '0px',
                      color: '#d68904',
                      fontWeight: 'bold',
                      opacity: celebrity ? '1' : '0',
                    }}
                  >
                    *{celebrity || ''}*
                  </div>
                </div>
                <div className="user-details-bubble-container">
                  <div className="user-details-bubble">
                    {grade || 'no grade'}
                  </div>
                  <div className="user-details-bubble">
                    {major || 'no major'}
                  </div>
                  <div
                    className="user-details-bubble"
                    style={{ color: '#724FCC', borderColor: '#724FCC' }}
                  >
                    {school || 'no school'}
                  </div>
                </div>
              </div>
            </div>

            {onViewHistory && data.user && (
              <div className="user-moderation-actions">
                <span>用户内容</span>
                <Button icon="history" onClick={onViewHistory}>查看发帖历史</Button>
              </div>
            )}
            {allowBan && data.user && (
              <UserBanAction key={_id} client={client} userId={_id} nickname={nickname} />
            )}
            <div className="user-details-below-container">
              {identity ? (
                <div className="user-identity-container">
                  <div className="user-identity-container-school">
                    {identity.school}
                  </div>
                  <div className="user-identity-container-Id">
                    {identity.studentId}
                  </div>
                  <div className="user-identity-container-Id">
                    {phone}
                  </div>
                </div>
              ) : (
                <div className="user-noIdentity-container">
                  <div className="user-noIdentity">
                    The user's identity is not verified!
                  </div>
                </div>
              )}
            </div>
            {type === 'View' && (
              <div className="user-details-below-container">
                <div className="user-details-counter-container">
                  <div className="user-details-counter-title">
                    Followers ({followers.length})
                  </div>
                  {followers.slice(0, 8).map((follower, index) => (
                    <img
                      className="user-details-counter-avatar"
                      key={index}
                      src={follower.avatarUrl}
                      alt=""
                    />
                  ))}
                </div>

                <div className="user-details-counter-container">
                  <div className="user-details-counter-title">
                    Following users ({following.length})
                  </div>
                  {following.slice(0, 8).map((idol, index) => (
                    <img
                      className="user-details-counter-avatar"
                      key={index}
                      src={idol.avatarUrl}
                      alt=""
                    />
                  ))}
                </div>

                <div className="user-details-counter-container">
                  <div className="user-details-counter-title">
                    Following cartons ({cartons.length})
                  </div>
                  {cartons.slice(0, 8).map((carton, index) => (
                    <img
                      className="user-details-counter-avatar"
                      key={index}
                      src={carton.imgUrl}
                      alt=""
                    />
                  ))}
                </div>
                {agreeTerms ? (
                  <div className="user-details-unban-message">
                    {' '}
                    *User has agreed to the terms and services*
                  </div>
                ) : (
                  <div className="user-details-ban-message">
                    {' '}
                    *User has NOT agreed to the terms and services*
                  </div>
                )}
              </div>
            )}

            {type === 'Ban' &&
              ('banned' ? (
                <div className="user-details-unban-message">
                  {' '}
                  *Are you sure you want to unban {nickname}?*
                </div>
              ) : (
                <div className="user-details-ban-message">
                  {' '}
                  *Are you sure you want to ban {nickname}?*
                  <DayPicker
                    onDayClick={handleDateClick}
                    selectedDays={dateValue || new Date()}
                    className="user-details-calendar"
                  />
                </div>
              ))}
          </div>
        );
      }}
    </Query>
  );
}

export default withApollo(UserDetail);
